import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FaCheck, FaLightbulb, FaGlobe, FaRocket, FaCode, FaNetworkWired, FaUsers, FaHandshake } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';
import logo from '../assets/logo.png';

const PageContainer = styled.div`
  padding-top: 2rem;
  background: linear-gradient(135deg, rgba(0, 213, 146, 0.03) 0%, rgba(0, 213, 146, 0) 100%);
  position: relative;
  overflow-x: hidden;
  
  &::before {
    content: '';
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d592' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    z-index: -1;
    pointer-events: none;
    opacity: 0.8;
  }
`;

const Section = styled.section`
  padding: 7rem 0;
  background-color: ${props => props.alternate ? 'rgba(240, 247, 255, 0.7)' : props.theme.colors.white};
  position: relative;
  overflow: hidden;
  
  ${props => props.alternate && `
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: 
        radial-gradient(circle at 15% 85%, rgba(0, 213, 146, 0.08) 0%, transparent 40%),
        radial-gradient(circle at 85% 15%, rgba(0, 213, 146, 0.08) 0%, transparent 40%);
      z-index: 0;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(0, 213, 146, 0.3) 50%, 
        transparent 100%
      );
    }
  `}
  
  ${props => props.futuristic && `
    background: linear-gradient(135deg, #f8fcff 0%, #f0f7fa 100%);
    
    &::before {
      content: '';
      position: absolute;
      width: 200%;
      height: 200%;
      top: -50%;
      left: -50%;
      background: radial-gradient(circle at center, rgba(0, 213, 146, 0.08) 0%, transparent 60%);
      animation: rotate 60s linear infinite;
      z-index: 1;
    }
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: 
        linear-gradient(135deg, rgba(0, 213, 146, 0.05) 0%, transparent 100%),
        repeating-linear-gradient(
          45deg,
          rgba(0, 213, 146, 0.02),
          rgba(0, 213, 146, 0.02) 1px,
          transparent 1px,
          transparent 10px
        );
      pointer-events: none;
      z-index: 2;
    }
    
    @keyframes rotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  `}
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutImageContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    order: 1;
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.3) 0%, transparent 70%);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    z-index: -1;
    animation: pulse 7s ease-in-out infinite alternate;
    filter: blur(30px);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    border: 1px dashed rgba(0, 213, 146, 0.5);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
    animation: rotate 30s linear infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 0.5;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.2);
      opacity: 0.8;
    }
  }
  
  @keyframes rotate {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const AboutImage = styled.img`
  max-width: 80%;
  filter: drop-shadow(0 0 40px rgba(0, 213, 146, 0.4));
  position: relative;
  z-index: 2;
  transform-style: preserve-3d;
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(0, 213, 146, 0.05);
    top: 20px;
    left: 20px;
    filter: blur(20px);
    z-index: -1;
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) rotate(0deg);
    }
    50% {
      transform: translateY(-15px) rotate(2deg);
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    max-width: 60%;
  }
`;

const AboutContent = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    order: 2;
    text-align: center;
  }
`;

const AboutDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  line-height: 1.7;
`;

const FeaturesList = styled.ul`
  list-style: none;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;
  color: ${props => props.theme.colors.gray};
  position: relative;
  padding-left: 1rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const FeatureIcon = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 1.3rem;
  margin-right: 1rem;
  background: rgba(0, 213, 146, 0.1);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(0, 213, 146, 0.3);
`;

const MissionVisionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MissionVisionCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 213, 146, 0.1);
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, transparent);
    z-index: 1;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    display: flex;
    align-items: center;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 1.5rem;
    font-size: 1.5rem;
    position: relative;
  }
  
  svg {
    color: ${props => props.theme.colors.primary};
    margin-right: 1.2rem;
    font-size: 2rem;
    filter: drop-shadow(0 0 10px rgba(0, 213, 146, 0.3));
  }
  
  p {
    color: ${props => props.theme.colors.gray};
    line-height: 1.7;
    position: relative;
    z-index: 2;
  }
`;

const RoadmapContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  max-width: 900px;
  margin: 4rem auto 2rem;
  position: relative;
  padding: 0 1rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2300d592' fill-opacity='0.02' fill-rule='evenodd'%3E%3Cpath d='M0 20L20 0h20v20L20 40H0V20zm10 10l10-10l10 10l-10 10L10 30z'/%3E%3C/g%3E%3C/svg%3E");
    z-index: -1;
    opacity: 0.3;
  }
`;

const RoadmapCard = styled.div`
  display: flex;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 213, 146, 0.1);
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.05) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 0;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const RoadmapIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(120deg, ${props => props.theme.colors.primary}, #00a080);
  color: white;
  min-width: 150px;
  position: relative;
  z-index: 1;
  
  svg {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.4));
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.1) 1px,
      transparent 1px,
      transparent 6px
    );
    z-index: -1;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: 1.5rem;
    min-width: 100%;
    flex-direction: row;
    justify-content: space-between;
    
    svg {
      margin-bottom: 0;
      margin-right: 1rem;
      font-size: 1.8rem;
    }
  }
`;

const RoadmapPhase = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  opacity: 0.9;
`;

const RoadmapContent = styled.div`
  padding: 2rem;
  flex: 1;
  position: relative;
  z-index: 1;
`;

const RoadmapTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 600;
`;

const RoadmapDate = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  margin-bottom: 1rem;
  display: inline-block;
  padding: 0.3rem 0.8rem;
  background-color: rgba(0, 213, 146, 0.1);
  border-radius: 20px;
`;

const RoadmapDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const RoadmapProgress = styled.div`
  height: 6px;
  width: 100%;
  background-color: #f1f1f1;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.completed}%;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, #00a080);
    border-radius: 10px;
    transition: width 1.5s ease-in-out;
  }
  
  &::after {
    content: '${props => props.completed}% Completed';
    position: absolute;
    right: 0;
    top: -20px;
    font-size: 0.8rem;
    color: ${props => props.theme.colors.gray};
  }
`;


const About = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>About Us | RDT Chain - Mission, Vision & Roadmap</title>
      </Helmet>
      <Section futuristic>
        <Container>
          <SectionTitle
            subtitle="About RDT Chain"
            title="Revolutionizing Global Payments"
            description="Experience the future of finance with our innovative blockchain technology that enables instant, secure, and fee-less cross-border payments."
            center
          />

          <AboutGrid>
            <AboutContent>
              <AboutDescription>
                RDT Chain is a cutting-edge blockchain platform engineered specifically to eliminate transaction fees while
                enabling lightning-fast cross-border payments. Our advanced Proof of Authority (PoA) consensus protocol
                delivers exceptional energy efficiency and unprecedented scalability for global financial transactions.
              </AboutDescription>

              <AboutDescription>
                Designed with user experience at its core, RDT Chain provides seamless compatibility with existing
                Ethereum tools and wallets, creating a frictionless experience for users and developers alike. Our
                proprietary architecture ensures transaction finality in seconds rather than minutes or hours.
              </AboutDescription>

              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheck /></FeatureIcon>
                  <span>Zero transaction fees - completely eliminating costs</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheck /></FeatureIcon>
                  <span>Lightning-fast 5-second block time for near-instant confirmations</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheck /></FeatureIcon>
                  <span>Energy-efficient PoA consensus reducing carbon footprint by 99.9%</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheck /></FeatureIcon>
                  <span>Full EVM compatibility with advanced smart contract capabilities</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheck /></FeatureIcon>
                  <span>Quantum-resistant encryption for future-proof security</span>
                </FeatureItem>
              </FeaturesList>

              <Button to="/developers">Start Building on RDT Chain</Button>
            </AboutContent>

            <AboutImageContainer>
              <AboutImage src={logo} alt="RDT Chain" />
            </AboutImageContainer>
          </AboutGrid>
        </Container>
      </Section>

      <Section alternate>
        <Container>
          <SectionTitle
            subtitle="Our Purpose"
            title="Mission & Vision"
            description="Our guiding principles and long-term goals for RDT Chain."
            center
          />

          <MissionVisionContainer>
            <MissionVisionCard>
              <h3>
                <FaLightbulb />
                Our Mission
              </h3>
              <p>
                To create a blockchain platform that enables borderless, instant, and fee-less payments for everyone,
                regardless of location or economic status. We aim to break down financial barriers and empower individuals
                and businesses with accessible blockchain technology.
              </p>
            </MissionVisionCard>

            <MissionVisionCard>
              <h3>
                <FaGlobe />
                Our Vision
              </h3>
              <p>
                A world where financial transactions are frictionless, secure, and accessible to all.
                We envision RDT Chain becoming the backbone of a truly global payment system that connects
                people across borders without the limitations of traditional banking systems.
              </p>
            </MissionVisionCard>
          </MissionVisionContainer>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionTitle
            subtitle="Our Journey"
            title="RDT Chain Roadmap"
            description="Key milestones in our development and future plans."
            center
          />

          <RoadmapContainer>
            <RoadmapCard>
              <RoadmapIcon>
                <FaRocket />
                <RoadmapPhase>Phase 1</RoadmapPhase>
              </RoadmapIcon>
              <RoadmapContent>
                <RoadmapTitle>Project Inception</RoadmapTitle>
                <RoadmapDate>Completed</RoadmapDate>
                <RoadmapDescription>
                  Initial concept development and team formation. Research on zero-fee blockchain architectures
                  and cross-border payment solutions. Established core technology principles and created
                  initial whitepaper outlining our vision and tokenomics.
                </RoadmapDescription>
                <RoadmapProgress completed={100} />
              </RoadmapContent>
            </RoadmapCard>

            <RoadmapCard>
              <RoadmapIcon>
                <FaCode />
                <RoadmapPhase>Phase 2</RoadmapPhase>
              </RoadmapIcon>
              <RoadmapContent>
                <RoadmapTitle>Core Technology Development</RoadmapTitle>
                <RoadmapDate>Completed</RoadmapDate>
                <RoadmapDescription>
                  Development of core blockchain infrastructure with PoA consensus mechanism.
                  Creation of comprehensive developer documentation and establishment of strategic
                  partnerships. Testing network performance to ensure 5-second block time and EVM compatibility.
                </RoadmapDescription>
                <RoadmapProgress completed={85} />
              </RoadmapContent>
            </RoadmapCard>

            <RoadmapCard>
              <RoadmapIcon>
                <FaNetworkWired />
                <RoadmapPhase>Phase 3</RoadmapPhase>
              </RoadmapIcon>
              <RoadmapContent>
                <RoadmapTitle>Wallet & Explorer Launch</RoadmapTitle>
                <RoadmapDate>Completed</RoadmapDate>
                <RoadmapDescription>
                  Launch of official RDT Chain wallet applications for iOS and Android with secure key management
                  and intuitive interface. Release of blockchain explorer with comprehensive transaction tracking
                  and analytics. Implementation of seamless wallet connectivity with MetaMask integration.
                </RoadmapDescription>
                <RoadmapProgress completed={40} />
              </RoadmapContent>
            </RoadmapCard>

            <RoadmapCard>
              <RoadmapIcon>
                <FaUsers />
                <RoadmapPhase>Phase 4</RoadmapPhase>
              </RoadmapIcon>
              <RoadmapContent>
                <RoadmapTitle>Ecosystem Expansion</RoadmapTitle>
                <RoadmapDate>Completed</RoadmapDate>
                <RoadmapDescription>
                  Development of DeFi protocols, payment gateways, and merchant tools powered by RDT Chain.
                  Implementation of cross-chain bridges for interoperability with other blockchains.
                  Strategic partnerships with financial institutions and establishment of comprehensive developer grant program.
                </RoadmapDescription>
                <RoadmapProgress completed={10} />
              </RoadmapContent>
            </RoadmapCard>

            <RoadmapCard>
              <RoadmapIcon>
                <FaHandshake />
                <RoadmapPhase>Phase 5</RoadmapPhase>
              </RoadmapIcon>
              <RoadmapContent>
                <RoadmapTitle>Global Payment Network</RoadmapTitle>
                <RoadmapDate>Long-term Vision</RoadmapDate>
                <RoadmapDescription>
                  Establishment of a global, decentralized payment network powered by RDT Chain technology.
                  Integration with traditional banking systems, mobile money platforms, and e-commerce solutions worldwide.
                  Creation of financial inclusion initiatives to bring blockchain benefits to underserved markets.
                </RoadmapDescription>
                <RoadmapProgress completed={5} />
              </RoadmapContent>
            </RoadmapCard>
          </RoadmapContainer>
        </Container>
      </Section>

    </PageContainer>
  );
};

export default About;